import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  isSuperadmin,
  isAdministrator,
  isManager,
  hasAccessToAllProperties } from
'../utils/roleCheck';

const BuildingContext = createContext(null);

export const BuildingProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentBuilding, setCurrentBuilding] = useState(null);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper to create virtual building objects
  const getVirtualBuilding = (type, propertyId) => {
    if (type === 'all') {
      return { id: 'all', name: 'All', isAll: true, property_id: propertyId };
    }
    if (type === 'property') {
      return { id: 'property', name: 'Property Wide', isPropertyWide: true, property_id: propertyId };
    }
    return null;
  };

  useEffect(() => {
    if (user) {
      loadBuildingsAndProperties();
    }
  }, [user]);

  const loadBuildingsAndProperties = async (userOverride = null) => {
    try {
      setLoading(true);
      const activeUser = userOverride || user;

      if (!activeUser) return;

      const userRole = activeUser.role;

      // 1. SuperAdmins and Administrators: Access to EVERYTHING
      if (hasAccessToAllProperties(userRole)) {
        const [propsRes, bldgsRes] = await Promise.all([
        window.ezsite.apis.tablePage(70646, { PageNo: 1, PageSize: 100, OrderByField: 'name', IsAsc: true }),
        window.ezsite.apis.tablePage(70647, { PageNo: 1, PageSize: 500, OrderByField: 'name', IsAsc: true })]
        );

        const props = propsRes?.data?.List || [];
        const buildings = bldgsRes?.data?.List || [];

        setAvailableProperties(props);
        setAvailableBuildings(buildings);

        // Set default property
        const defaultProp = props.find((p) => p.id === activeUser.property_id) || props[0];
        setCurrentProperty(defaultProp);

        if (defaultProp) {
          // Default to "All" instead of a specific building
          setCurrentBuilding(getVirtualBuilding('all', defaultProp.id));
        }
      }
      // 2. Managers: Access to all buildings in their assigned property
      else if (isManager(userRole)) {
        const [propRes, bldgsRes] = await Promise.all([
        window.ezsite.apis.tablePage(70646, {
          PageNo: 1, PageSize: 1,
          Filters: [{ name: 'id', op: 'Equal', value: activeUser.property_id }]
        }),
        window.ezsite.apis.tablePage(70647, {
          PageNo: 1, PageSize: 100,
          Filters: [{ name: 'property_id', op: 'Equal', value: activeUser.property_id }]
        })]
        );

        const props = propRes?.data?.List || [];
        const buildings = bldgsRes?.data?.List || [];

        setAvailableProperties(props);
        setAvailableBuildings(buildings);
        setCurrentProperty(props[0]);

        // Default to "All"
        if (props[0]) {
          setCurrentBuilding(getVirtualBuilding('all', props[0].id));
        }
      }
      // 3. Residents / General Users: Access to all buildings in their property
      else {
        const [bldgRes, propRes] = await Promise.all([
        window.ezsite.apis.tablePage(70647, {
          PageNo: 1, PageSize: 100,
          Filters: [{ name: 'property_id', op: 'Equal', value: activeUser.property_id }]
        }),
        window.ezsite.apis.tablePage(70646, {
          PageNo: 1, PageSize: 1,
          Filters: [{ name: 'id', op: 'Equal', value: activeUser.property_id }]
        })]
        );

        const buildings = bldgRes?.data?.List || [];
        const props = propRes?.data?.List || [];

        setAvailableBuildings(buildings);
        setAvailableProperties(props);
        setCurrentProperty(props[0]);

        // Residents also default to "All" to see property-wide announcements/topics
        if (props[0]) {
          setCurrentBuilding(getVirtualBuilding('all', props[0].id));
        }
      }
    } catch (error) {
      console.error('Failed to load buildings/properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchBuilding = (buildingId) => {
    // Handle virtual buildings
    if (buildingId === 'all') {
      setCurrentBuilding(getVirtualBuilding('all', currentProperty?.id));
      return;
    }
    if (buildingId === 'property') {
      setCurrentBuilding(getVirtualBuilding('property', currentProperty?.id));
      return;
    }

    const building = availableBuildings.find((b) => b.id === buildingId);
    if (building) {
      setCurrentBuilding(building);
      // If the building belongs to a different property, switch that too
      if (currentProperty?.id !== building.property_id) {
        const property = availableProperties.find((p) => p.id === building.property_id);
        if (property) setCurrentProperty(property);
      }
    }
  };

  const switchProperty = (propertyId) => {
    const property = availableProperties.find((p) => p.id === propertyId);
    if (property) {
      setCurrentProperty(property);
      // Auto-select "All" for the new property
      setCurrentBuilding(getVirtualBuilding('all', propertyId));
    }
  };

  return (
    <BuildingContext.Provider value={{
      currentBuilding,
      currentProperty,
      availableBuildings,
      availableProperties,
      loading,
      switchBuilding,
      switchProperty,
      refresh: loadBuildingsAndProperties
    }}>
      {children}
    </BuildingContext.Provider>);

};

export const useBuilding = () => {
  const context = useContext(BuildingContext);
  if (!context) throw new Error('useBuilding must be used within BuildingProvider');
  return context;
};